/**
 * Locale de l'interface : sens de lecture, locale Ant Design et locale dayjs,
 * dérivés d'une seule source — la langue i18next (F-FE-05, MON-15).
 *
 * Avant : `ConfigProvider` sans `direction` ni `locale` → l'arabe s'affichait
 * de gauche à droite et le DatePicker restait en anglais.
 */
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import 'dayjs/locale/en-gb';
import type { Locale } from 'antd/es/locale';
import frFR from 'antd/locale/fr_FR';
import enGB from 'antd/locale/en_GB';
import arEG from 'antd/locale/ar_EG';
import i18n from 'i18next';

export type Direction = 'ltr' | 'rtl';
export type UiLang = 'fr' | 'en' | 'ar';

/** Langue de base (`ar-MA` → `ar`) ; hors des trois langues servies → fr (fallbackLng d'i18n). */
export const uiLangOf = (language: string | undefined): UiLang => {
    const base = (language ?? '').toLowerCase().replace('_', '-').split('-')[0];
    return base === 'ar' || base === 'en' || base === 'fr' ? base : 'fr';
};

export const directionOf = (language: string | undefined): Direction => (uiLangOf(language) === 'ar' ? 'rtl' : 'ltr');

/** Ant Design ne fournit pas de locale ar_MA : ar_EG est la locale arabe livrée. */
export const ANTD_LOCALES: Record<UiLang, Locale> = { fr: frFR, en: enGB, ar: arEG };
export const DAYJS_LOCALES: Record<UiLang, string> = { fr: 'fr', en: 'en-gb', ar: 'ar' };

export type UiLocale = { lang: UiLang; direction: Direction; antdLocale: Locale };

/**
 * Applique la langue au document (`dir` et `lang` sur <html>, pour que les
 * portails d'Ant Design — infobulles, menus — héritent du sens) et à dayjs.
 */
export const applyUiLocale = (language: string | undefined): UiLocale => {
    const lang = uiLangOf(language);
    const direction = directionOf(lang);
    dayjs.locale(DAYJS_LOCALES[lang]);
    if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.setAttribute('dir', direction);
        document.documentElement.setAttribute('lang', lang);
    }
    return { lang, direction, antdLocale: ANTD_LOCALES[lang] };
};

/** Suit les changements de langue en cours de session (sans rechargement). */
export const useUiLocale = (): UiLocale => {
    const [locale, setLocale] = useState<UiLocale>(() => applyUiLocale(i18n.language));
    useEffect(() => {
        const onChange = (language: string) => setLocale(applyUiLocale(language));
        i18n.on('languageChanged', onChange);
        // La langue a pu être résolue entre le premier rendu et cet effet.
        setLocale(applyUiLocale(i18n.language));
        return () => {
            i18n.off('languageChanged', onChange);
        };
    }, []);
    return locale;
};
